import {ReactNode, useEffect} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import queryString from 'query-string';
import url from 'url';
import {ListTable} from './ListTable';
import {ListPagination} from './ListPagination';
import NoItems from './no_items/NoItems';
import Container from './layout/Container';
import useFetch from '../../hooks/useFetch';
import Error from './Error';
import {listData} from '../../constants/listData';
import {getSortedParams} from '../../helpers';
import Spinner from './Spinner';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {setTitle} from '../../store/header/header.slice';
import {Query} from '../../constants/types';
import {noItemsMessages} from '../../constants/constants';
import ListHeader from './ListHeader';

const DEFAULT_QUERY = {
  direction: 'asc',
  page: '1',
  per_page: '10',
  simple_search: '',
  sort: ''
};

type ListProps = {
  filters?: ReactNode,
  rowRenderer?: (content: any) => ReactNode,
  toggleRefreshPage?: boolean,
  initialQuery?: Query
}

export const List = ({
  initialQuery = DEFAULT_QUERY, rowRenderer, filters, toggleRefreshPage
}: ListProps) => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const {pathname, search} = useLocation();
  const {activeTenant} = useAppSelector(({permissions}) => permissions);
  const query = {
    ...initialQuery,
    ...getSortedParams(queryString.parse(search))
  };

  useEffect(() => {
    history.replace(url.format({pathname, query}));
    dispatch(setTitle(''));
  }, []);

  const entity = listData[pathname];

  if (!query.sort) {
    query.sort = entity?.defaultSort;
  }

  const [data, isLoading, error] = useFetch(entity.fetchUrl(), [
    toggleRefreshPage, query
  ], query);

  useEffect(() => {
    if ((query.page !== '1' || query.simple_search) && data) {
      history.push(url.format({
        pathname, query: {...query, page: 1, simple_search: ''}}));
    }
  }, [activeTenant]);

  if (error) {
    return <Error message={error?.message} />;
  }

  const {
    content = [],
    pageNumber = 1,
    totalElements = 0,
    totalPages = 1
  } = data || {};
  const contentLength = content?.length;

  const noItems = !totalElements && !query?.simple_search && !isLoading;
  const noSearchResults = !totalElements &&
    !!query?.simple_search &&
    !isLoading;

  const tableContent = isLoading && !data ?
    (<Spinner loadingText={entity?.loadingText}/>) :
    (<Spinner loadingText={entity?.loadingText} isLoading={isLoading}>
      <>
        <ListTable
          entity={entity}
          content={content}
          query={query}
          rowRenderer={rowRenderer}
        />

        {noSearchResults ?
            (<NoItems message={noItemsMessages.SEARCH}/>) :
           noItems ?
             (<NoItems message={noItemsMessages.ITEMS}/>) :
             (<ListPagination
               contentLength={contentLength}
               pageNumber={pageNumber}
               totalElements={totalElements}
               totalPages={totalPages}
               query={query}
             />)
        }
      </>
    </Spinner>);

  return (
    <Container header={entity?.header}>
      <>
        <ListHeader entity={entity} query={query} filters={filters} />
        {tableContent}
      </>
    </Container>
  );
};

export default List;
